import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
  orderStatusCodes,
  type OrderFilters,
  type OrderPeriod,
  type OrderSort,
  type OrderStatusCode,
  type PaymentMethod,
} from '@/features/orders/api';
import { OrdersPage } from '@/features/orders/OrdersPage';

const periods: OrderPeriod[] = ['all', 'today', 'week', 'month'];
const sorts: OrderSort[] = ['NEWEST', 'ATTENTION_FIRST'];
const paymentMethods: PaymentMethod[] = ['CARD', 'ERIP', 'CASH_ON_DELIVERY'];

/** Порядок по умолчанию — свежие заказы сверху; очередь «требующих внимания» включают руками. */
const DEFAULT_SORT: OrderSort = 'NEWEST';

export interface OrdersSearch {
  /** Статусы через запятую: «status=NEW,UNDEFINED». Пусто — все семь. */
  status?: string;
  period?: OrderPeriod;
  payment?: PaymentMethod;
  /** Код способа доставки из снимка заказа. */
  delivery?: string;
  /** Строка поиска: номер заказа, телефон получателя или начало его фамилии. */
  q?: string;
  sort?: OrderSort;
  /** Открытая карточка заказа — в адресе, потому что ссылку на заказ пересылают коллеге. */
  order?: string;
}

/**
 * Состояние раздела живёт в адресе: ссылку на «новые и неопределённые за сегодня» или прямо
 * на карточку заказа пересылают коллеге, и открыться она должна тем же экраном.
 *
 * Чужие значения отбрасываются: статусы и способы уходят в запрос как есть, а незнакомое
 * значение бэкенд отклонит как `400 bad-request`.
 */
export const Route = createFileRoute('/_app/orders')({
  validateSearch: (search: Record<string, unknown>): OrdersSearch => {
    const statuses = parseStatuses(search.status);
    return {
      status: statuses.length > 0 ? statuses.join(',') : undefined,
      period: periods.find((value) => value === search.period && value !== 'all'),
      payment: paymentMethods.find((value) => value === search.payment),
      delivery:
        typeof search.delivery === 'string' && search.delivery !== ''
          ? search.delivery.toUpperCase()
          : undefined,
      q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
      sort: sorts.find((value) => value === search.sort && value !== DEFAULT_SORT),
      order: typeof search.order === 'string' && search.order !== '' ? search.order : undefined,
    };
  },
  component: OrdersRoute,
});

/**
 * Статусы из адреса. Неизвестные коды выбрасываются молча, повторы схлопываются: список
 * уходит в запрос как есть, и «status=NEW&status=NEW» бэкенду ничего не сообщает.
 */
function parseStatuses(raw: unknown): OrderStatusCode[] {
  if (typeof raw !== 'string' || raw === '') {
    return [];
  }
  const wanted = new Set(raw.toUpperCase().split(','));
  return orderStatusCodes.filter((status) => wanted.has(status));
}

function OrdersRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // Стабильная ссылка: страница держит на этом колбэке таймер задержки поиска, и новая функция
  // на каждый рендер сбрасывала бы отсчёт — строка уходила бы в адрес только после паузы.
  const onFiltersChange = useCallback(
    (next: Partial<OrderFilters>) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          status: 'statuses' in next ? next.statuses?.join(',') || undefined : previous.status,
          period: 'period' in next ? (next.period === 'all' ? undefined : next.period) : previous.period,
          payment: 'paymentMethod' in next ? (next.paymentMethod ?? undefined) : previous.payment,
          delivery: 'deliveryMethod' in next ? (next.deliveryMethod ?? undefined) : previous.delivery,
          q: 'search' in next ? next.search || undefined : previous.q,
          sort: 'sort' in next ? (next.sort === DEFAULT_SORT ? undefined : next.sort) : previous.sort,
        }),
        // Правка отбора — не шаг навигации: иначе «назад» отматывал бы по букве запроса.
        replace: true,
      });
    },
    [navigate],
  );

  const onOrderChange = useCallback(
    (id: string | null) => {
      void navigate({
        search: (previous) => ({ ...previous, order: id ?? undefined }),
        // Открытие и закрытие карточки — шаг истории: «назад» из карточки возвращает
        // к списку, как и ожидается от бокового экрана, открытого по ссылке.
        replace: false,
      });
    },
    [navigate],
  );

  /** Переход к покупателю (AUI-ORD-03): его карточка живёт в разделе «Покупатели». */
  const onCustomerOpen = useCallback(
    (customerId: string) => {
      void navigate({ to: '/users', search: { customer: customerId } });
    },
    [navigate],
  );

  return (
    <OrdersPage
      filters={{
        statuses: parseStatuses(search.status),
        period: search.period ?? 'all',
        paymentMethod: search.payment ?? null,
        deliveryMethod: search.delivery ?? null,
        search: search.q ?? '',
        sort: search.sort ?? DEFAULT_SORT,
      }}
      orderId={search.order ?? null}
      onFiltersChange={onFiltersChange}
      onOrderChange={onOrderChange}
      onCustomerOpen={onCustomerOpen}
    />
  );
}
